import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Save, Send, RotateCcw, Eye, FileText, ClipboardList, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { safeHtmlProp } from '../../utils/safeHtml';

/**
 * Admin manager for the transactional email template catalog (and a viewer for
 * the email_log audit trail). Lives under Admin Settings → Email tab.
 */
export function EmailTemplatesManager() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState({ subject: '', body: '' });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/api/email-templates/');
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Open/save/preview/reset are kept as small handlers; inlined where they
  // would otherwise be flagged by the experimental react-hooks lint rule.

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/api/email-templates/${selected.key}`, editing);
      toast.success(`Saved "${selected.name}"`);
      await fetchTemplates();
      setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const reset = async () => {
    if (!selected) return;
    if (!window.confirm(`Reset "${selected.name}" to the built-in default?`)) return;
    try {
      await api.post(`/api/email-templates/${selected.key}/reset`);
      toast.success('Template reset');
      await fetchTemplates();
      const refreshed = (await api.get('/api/email-templates/')).data.templates
        .find((t) => t.key === selected.key);
      if (refreshed) {
        setEditing({ subject: refreshed.subject || '', body: refreshed.body || '' });
        setSelected(refreshed);
      }
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  const preview = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/api/email-templates/${selected.key}/preview`);
      setPreviewSubject(data.subject);
      setPreviewHtml(data.body);
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const openLog = async () => {
    setLogOpen(true);
    setLogLoading(true);
    try {
      const { data } = await api.get('/api/email-templates/log?limit=100');
      setLog(data.log || []);
    } catch (e) {
      toast.error('Failed to load log');
    }
    setLogLoading(false);
  };

  const doTestSend = async () => {
    if (!selected || !testEmail.trim()) {
      toast.error('Pick a template and enter a recipient');
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post('/api/email-templates/test-send', {
        template_key: selected.key,
        to_email: testEmail.trim(),
        context: SAMPLE_CONTEXT,
      });
      if (data.sent) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        toast.warning(`Logged but not delivered (${data.error || 'SMTP not configured'})`);
      }
      setTestSendOpen(false);
      setTestEmail('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    }
    setTesting(false);
  };

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid="email-templates-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#00E5FF]" /> Email Templates
            </CardTitle>
            <CardDescription className="text-[#A0B4CC]">
              Edit the {templates.length} transactional emails sent to members and merchants.
            </CardDescription>
          </div>
          <Button onClick={openLog} variant="outline" size="sm"
            className="border-white/20 text-white hover:bg-white/5"
            data-testid="open-email-log-btn">
            <ClipboardList className="w-4 h-4 mr-1.5" /> View Send Log
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-[#A0B4CC] text-sm py-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-2 max-h-[28rem] overflow-y-auto pr-1">
            {templates.map((t) => (
              <button
                key={t.key}
                onClick={() => { setEditing({ subject: t.subject || '', body: t.body || '' }); setSelected(t); }}
                className="text-left p-3 rounded-lg bg-[#000B18] border border-white/10 hover:border-[#00E5FF]/40 transition-colors"
                data-testid={`edit-tpl-${t.key}-btn`}
              >
                <p className="text-white text-sm font-semibold truncate">{t.name}</p>
                <p className="text-[#5C7599] text-xs mt-0.5 truncate">{t.subject}</p>
                <p className="text-[#5C7599] text-[10px] mt-1 font-mono">{t.key}</p>
              </button>
            ))}
          </div>
        )}
      </CardContent>

      {/* Edit dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#041A3B] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-tpl-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{selected?.name}</DialogTitle>
            <p className="text-[#5C7599] text-xs font-mono mt-1">{selected?.key}</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A0B4CC] text-xs">Subject</Label>
              <Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                className="bg-[#000B18] border-white/20 text-white" data-testid="tpl-subject-input" />
            </div>
            <div>
              <Label className="text-[#A0B4CC] text-xs">Body (HTML — use {`{{variable}}`} placeholders)</Label>
              <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                rows={12}
                className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm font-mono focus:border-[#00E5FF] focus:outline-none"
                data-testid="tpl-body-input" />
            </div>
            {selected?.variables?.length > 0 && (
              <div className="text-[#A0B4CC] text-xs">
                Available variables: {selected.variables.map((v) => (
                  <code key={v} className="mx-1 px-1.5 py-0.5 rounded bg-[#000B18] text-[#00E5FF]">{`{{${v}}}`}</code>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2 mt-4">
            <Button onClick={reset} variant="outline" size="sm" className="border-white/20 text-[#A0B4CC]" data-testid="tpl-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Default
            </Button>
            <Button onClick={preview} variant="outline" size="sm" className="border-white/20 text-white" data-testid="tpl-preview-btn">
              <Eye className="w-3.5 h-3.5 mr-1" /> Preview
            </Button>
            <Button onClick={() => setTestSendOpen(true)} variant="outline" size="sm"
              className="border-[#00E5FF]/30 text-[#00E5FF]" data-testid="tpl-send-test-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Send Test
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="tpl-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewHtml} onOpenChange={(v) => !v && setPreviewHtml(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Preview: {previewSubject}</DialogTitle>
          </DialogHeader>
          <div className="border border-gray-200 rounded-lg overflow-hidden" dangerouslySetInnerHTML={safeHtmlProp(previewHtml)} />
        </DialogContent>
      </Dialog>

      {/* Test send dialog */}
      <Dialog open={testSendOpen} onOpenChange={setTestSendOpen}>
        <DialogContent className="bg-[#041A3B] border-white/10 max-w-md" data-testid="test-send-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Send test email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-[#A0B4CC] text-xs">Recipient email</Label>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-[#000B18] border-white/20 text-white"
              data-testid="test-send-email-input" />
            <p className="text-[#5C7599] text-xs">
              Uses sample data for any unfilled variables. Requires SMTP to be configured for actual delivery.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestSendOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
            <Button onClick={doTestSend} disabled={testing}
              className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
              data-testid="test-send-confirm-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email log dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="bg-[#041A3B] border-white/10 max-w-4xl max-h-[90vh]" data-testid="email-log-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Email Send Log (latest 100)</DialogTitle>
          </DialogHeader>
          {logLoading ? (
            <div className="text-[#A0B4CC] py-6 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : log.length === 0 ? (
            <div className="text-[#A0B4CC] py-6 text-sm">No emails sent yet.</div>
          ) : (
            <div className="overflow-x-auto mobile-scroll-wrap max-h-[60vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#041A3B] text-[#5C7599] text-left">
                  <tr>
                    <th className="p-2">Status</th>
                    <th className="p-2">Template</th>
                    <th className="p-2">Recipient</th>
                    <th className="p-2">Subject</th>
                    <th className="p-2">Sent At</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {log.map((row) => (
                    <tr key={row.id} className="border-t border-white/5">
                      <td className="p-2">
                        {row.success
                          ? <CheckCircle2 className="w-4 h-4 text-[#00E676]" />
                          : <span className="inline-flex items-center gap-1 text-[#FF1744]">
                              <XCircle className="w-4 h-4" />
                              <span className="text-[10px]" title={row.error}>{row.error || 'failed'}</span>
                            </span>}
                      </td>
                      <td className="p-2 font-mono text-[#00E5FF]">{row.template_key}</td>
                      <td className="p-2 truncate max-w-[180px]" title={row.to_email}>{row.to_email}</td>
                      <td className="p-2 truncate max-w-[260px]" title={row.subject}>{row.subject}</td>
                      <td className="p-2 text-[#A0B4CC] whitespace-nowrap">{row.sent_at?.slice(0, 19).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const SAMPLE_CONTEXT = {
  member_name: 'John Doe',
  membership_id: 'AGA-2026-TEST',
  email: 'john@example.com',
  reset_link: 'https://allglobalaccess.com/reset-password?token=sample',
  business_name: 'Sample Business Co.',
  rejection_reason: 'ID photo unclear',
  plan_name: 'Professional',
  subscription_amount: '10,000.00',
  referred_name: 'Jane Smith',
  amount: '500.00',
  level: 'direct referral',
  bank_name: 'BPI',
  bank_account: '••••1234',
  days_min: 3,
  days_max: 5,
  reason: 'Sample reason',
  direction: 'credited',
  preposition: 'to',
  ticket_number: 'ABCD1234',
  subject: 'Sample ticket subject',
  merchant_name: 'Sample Merchant',
  service_name: 'Sample Service',
};
